/**
 * Utility functions for building URL query parameters
 */

/**
 * Convert camelCase to snake_case
 */
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

/**
 * Build URLSearchParams from an object of parameters
 * Automatically filters out undefined, null, and empty string values
 * Converts camelCase keys to snake_case for API compatibility
 *
 * @param params - Object containing query parameters
 * @returns URLSearchParams ready to be used in fetch requests
 *
 * @example
 * const query = buildQueryParams({ page: 1, search: 'test', campId: '123', status: undefined })
 * // Returns: URLSearchParams with "page=1&search=test&camp_id=123" (status excluded, campId converted)
 */
type QueryParamValue = string | number | boolean | Date | null | undefined | string[] | number[] | boolean[]

type QueryParamsInput = Record<string, unknown>

function normalizeQueryValue(value: unknown): QueryParamValue | undefined {
  if (value === undefined || value === null || value === '')
    return undefined

  if (value instanceof Date)
    return value

  if (Array.isArray(value))
    return value as QueryParamValue

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value

  return undefined
}

export function buildQueryParams<T extends object>(params: T): URLSearchParams {
  const query = new URLSearchParams()

  Object.entries(params as QueryParamsInput).forEach(([key, value]) => {
    const normalizedValue = normalizeQueryValue(value)

    // Only append if value is not null, undefined, or empty string
    if (normalizedValue !== undefined) {
      const snakeKey = camelToSnake(key)

      query.append(snakeKey, String(normalizedValue))
    }
  })

  return query
}

/**
 * Build query string from an object of parameters
 * Wrapper around buildQueryParams that returns a string
 * Converts camelCase keys to snake_case for API compatibility
 *
 * @param params - Object containing query parameters
 * @returns Query string (e.g., "page=1&limit=10&camp_id=123")
 *
 * @example
 * const queryString = buildQueryString({ page: 1, limit: 10, campId: '123' })
 * // Returns: "page=1&limit=10&camp_id=123"
 */
export function buildQueryString<T extends object>(params: T): string {
  return buildQueryParams(params).toString()
}
